import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { download } from '../../services/util/miscellaneousService';
import {
  getDashboardErrorMessage,
  useDownloadDashboardImportTemplateMutation,
  useImportDashboardCsvMutation
} from './dashboardApi';
import DashboardApproverField from './DashboardApproverField';

export const DASHBOARD_IMPORT_MAXIMUM_FILE_SIZE_BYTES = 2 * 1024 * 1024;

export const validateDashboardImportFile = (file) => {
  if (!file) return 'Choose a CSV file to import.';
  if (!file.name?.toLowerCase().endsWith('.csv')) {
    return 'Choose a file with the .csv extension.';
  }
  if (file.size > DASHBOARD_IMPORT_MAXIMUM_FILE_SIZE_BYTES) {
    return 'The CSV file must be 2 MiB or smaller.';
  }
  return null;
};

const importPresentation = (status) => {
  if (status === 'complete') {
    return { severity: 'success', title: 'CSV import complete' };
  }
  if (status === 'partial') {
    return { severity: 'warning', title: 'CSV import partially complete' };
  }
  return { severity: 'error', title: 'CSV import failed' };
};

const countLabel = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const DashboardImportResult = ({ result }) => {
  const presentation = importPresentation(result.status);
  const summary = result.summary;
  const failedRows = (result.rows ?? []).filter(
    (row) => row.status === 'failed'
  );

  return (
    <Box data-testid="dashboard-import-result" sx={{ mt: 2 }}>
      <Alert severity={presentation.severity}>
        <AlertTitle>{presentation.title}</AlertTitle>
        {summary.importedRows} of {countLabel(summary.totalRows, 'row')}{' '}
        imported; {countLabel(summary.failedRows, 'row')} failed.{' '}
        {countLabel(summary.approvedValues, 'value')} approved and{' '}
        {countLabel(summary.pendingApprovalValues, 'value')} awaiting approval.
      </Alert>

      {failedRows.length > 0 && (
        <Paper
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', mt: 2, p: 2 }}
        >
          <Typography component="h4" fontWeight={700}>
            Rows requiring attention
          </Typography>
          <List dense disablePadding sx={{ mt: 1 }}>
            {failedRows.map((row) => (
              <ListItem
                alignItems="flex-start"
                disableGutters
                key={row.rowNumber}
              >
                <ListItemText
                  primary={`Row ${row.rowNumber}${
                    row.effectiveDate ? ` · ${row.effectiveDate}` : ''
                  }`}
                  secondary={
                    <Stack component="span" spacing={0.75} sx={{ mt: 0.75 }}>
                      {(row.errors ?? []).map((error, index) => (
                        <Box component="span" key={`${error.code}-${index}`}>
                          <Typography component="span" variant="body2">
                            {error.column ? `${error.column}: ` : ''}
                            {error.message}
                          </Typography>{' '}
                          <Chip
                            component="span"
                            label={error.code}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      ))}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

const DashboardCsvImport = ({
  dashboardVersionId,
  locationId,
  onImported = () => {}
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [result, setResult] = useState(null);
  const [approver, setApprover] = useState(null);
  const [approverError, setApproverError] = useState(null);
  const [downloadTemplate, downloadState] =
    useDownloadDashboardImportTemplateMutation();
  const [importCsv, importState] = useImportDashboardCsvMutation();
  const resetDownload = downloadState.reset;
  const resetImport = importState.reset;

  useEffect(() => {
    setSelectedFile(null);
    setFileError(null);
    setDownloadError(null);
    setResult(null);
    setApprover(null);
    setApproverError(null);
    resetDownload?.();
    resetImport?.();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [dashboardVersionId, locationId, resetDownload, resetImport]);

  const handleDownload = async () => {
    setDownloadError(null);
    resetDownload?.();
    try {
      const template = await downloadTemplate(locationId).unwrap();
      try {
        download(template.content, template.filename);
      } catch (_error) {
        setDownloadError('The CSV template could not be saved.');
      }
    } catch (_error) {
      // RTK Query exposes the normalized error through downloadState below.
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    const validationError = validateDashboardImportFile(file);
    setSelectedFile(validationError ? null : file);
    setFileError(validationError);
    setResult(null);
    resetImport?.();
  };

  const handleImport = async () => {
    const validationError = validateDashboardImportFile(selectedFile);
    setFileError(validationError);
    if (validationError) return;
    if (!approver?.user_id) {
      setApproverError('Select who should receive the approval email.');
      return;
    }

    setResult(null);
    try {
      const imported = await importCsv({
        locationId,
        file: selectedFile,
        approverUserId: approver.user_id
      }).unwrap();
      setResult(imported);
      setSelectedFile(null);
      setApprover(null);
      setApproverError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onImported(imported);
    } catch (_error) {
      // RTK Query exposes the normalized error through importState below.
    }
  };

  return (
    <Paper
      data-testid="dashboard-csv-import"
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', mt: 3, p: 3 }}
    >
      <Typography component="h2" fontSize={20} fontWeight={700}>
        Import dashboard data
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
        Download the current template, add up to 500 rows, then upload the CSV.
        The template is tied to this dashboard version and files must be 2 MiB
        or smaller.
      </Typography>

      <Stack
        alignItems={{ xs: 'stretch', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mt: 2 }}
      >
        <Button
          disabled={downloadState.isLoading}
          onClick={handleDownload}
          variant="outlined"
        >
          {downloadState.isLoading ? 'Downloading…' : 'Download CSV template'}
        </Button>
        <Button
          component="label"
          disabled={importState.isLoading}
          variant="outlined"
        >
          Choose CSV file
          <input
            accept=".csv,text/csv"
            aria-label="Dashboard CSV file"
            hidden
            onChange={handleFileChange}
            ref={fileInputRef}
            type="file"
          />
        </Button>
        <Button
          disabled={!selectedFile || importState.isLoading}
          onClick={handleImport}
          variant="contained"
        >
          {importState.isLoading ? 'Importing…' : 'Import CSV'}
        </Button>
        {(downloadState.isLoading || importState.isLoading) && (
          <CircularProgress size={22} />
        )}
      </Stack>

      <Box sx={{ mt: 2, maxWidth: 520 }}>
        <DashboardApproverField
          approver={approver}
          error={approverError}
          locationId={locationId}
          onChange={(value) => {
            setApprover(value);
            setApproverError(null);
          }}
        />
      </Box>

      {selectedFile && (
        <Typography sx={{ mt: 1.5 }} variant="body2">
          Selected file: {selectedFile.name}
        </Typography>
      )}
      {fileError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {fileError}
        </Alert>
      )}
      {(downloadError || downloadState.error) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {downloadError ||
            getDashboardErrorMessage(
              downloadState.error,
              'The CSV template could not be downloaded.'
            )}
        </Alert>
      )}
      {importState.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(
            importState.error,
            'The CSV file could not be imported.'
          )}
        </Alert>
      )}
      {result && <DashboardImportResult result={result} />}
    </Paper>
  );
};

export default DashboardCsvImport;
